// src/components/shared/SearchModal.jsx

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import { styled } from "@mui/system";
import { useMutation } from "@tanstack/react-query";
import { getWithQueryApiServices } from "../../apis/api";
import { apiRoutes } from "../../apis/apiPath";
import { errorToast } from "../../utils/helper";
import { useNavigate, useLocation } from "react-router-dom";
import { ROUTE_PATHS } from "../../routes/routePath";
import Cookies from "universal-cookie";
import { DateFilter } from "../pages/powerSearch/DateFilter";

const PRIMARY_COLOR = "#7285b3";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  zIndex: 1000,
  "& .MuiDialog-container": {
    overflow: "hidden",
  },
  "& .MuiDialog-paper": {
    backgroundColor: "transparent",
    boxShadow: "none",
    maxWidth: "none",
    width: "100vw",
    margin: 0,
    borderRadius: 0,
    marginTop: "56px",
    height: "calc(100vh - 56px)",
    maxHeight: "calc(100vh - 56px)",
    overflow: "hidden",
    [theme.breakpoints.up("sm")]: {
      marginTop: "64px",
      height: "calc(100vh - 64px)",
      maxHeight: "calc(100vh - 64px)",
    },
  },
  "& .MuiBackdrop-root": {
    marginTop: "56px",
    height: "calc(100vh - 56px)",
    [theme.breakpoints.up("sm")]: {
      marginTop: "64px",
      height: "calc(100vh - 64px)",
    },
  },
}));

// Styled Dialog for Advanced Search (Centered)
const AdvancedSearchDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    maxWidth: "95vw",
    maxHeight: "none",
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      width: "auto",
      minWidth: "600px",
      maxHeight: "95vh",
    },
    [theme.breakpoints.up("md")]: {
      width: "auto",
      minWidth: "850px",
      maxHeight: "95vh",
    },
    height: "auto",
    margin: "12px auto",
    borderRadius: "8px",
    backgroundColor: "#deedf5",
  },
  "& .MuiBackdrop-root": {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  backgroundColor: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(15px)",
  padding: "20px",
  position: "relative",
  overflowY: "auto",
  boxSizing: "border-box",
}));

const SearchBox = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "650px",
  position: "relative",
  backgroundColor: "white",
  borderRadius: "50px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  padding: "4px",
  transition: "box-shadow 0.3s ease",
  "&:hover": {
    boxShadow: "0 6px 30px rgba(0,0,0,0.15)",
  },
  "&:focus-within": {
    boxShadow: "0 6px 35px rgba(114, 133, 179, 0.3)",
  },
  [theme.breakpoints.down("sm")]: {
    borderRadius: "24px",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    fontSize: "18px",
    padding: "12px 112px 12px 20px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      padding: "6px 82px 6px 15px",
    },
    "& fieldset": {
      border: "none",
    },
  },
  "& .MuiInputBase-input": {
    padding: "14px 0",
    [theme.breakpoints.down("sm")]: {
      padding: "6px 0",
    },
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "20px",
  right: "20px",
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  zIndex: 10,
  "&:hover": {
    backgroundColor: "white",
  },
  [theme.breakpoints.down("sm")]: {
    top: "10px",
    right: "10px",
  },
}));

const AdvancedSearchButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: "60px",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: "#f5f5f5",
  color: "#666",
  padding: "10px",
  "&:hover": {
    backgroundColor: "#e0e0e0",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "5px",
    right: "42px",
  },
}));

const SubmitSearchButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: "8px",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: PRIMARY_COLOR,
  color: "white",
  padding: "10px",
  "&:hover": {
    backgroundColor: "#5d6d96",
  },
  "&.Mui-disabled": {
    backgroundColor: "#e0e0e0",
    color: "#a0a0a0",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "5px",
    right: "5px",
  },
}));

export const SearchModal = ({
  open,
  onClose,
  currentCompany,
  tenantString,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const cookies = new Cookies();

  // Auto-open Advanced Search Dialog if advancedSearch tour or autoSubmit is requested
  useEffect(() => {
    if (open) {
      const searchParams = new URLSearchParams(location.search);
      if (
        searchParams.get("startTour") === "advancedSearch" ||
        (searchParams.get("autoSubmit") === "true" &&
          searchParams.get("q") &&
          searchParams.get("q").trim().length > 0)
      ) {
        setAdvancedSearchOpen(true);
        const urlQ = searchParams.get("q");
        if (urlQ) {
          setSearchQuery(decodeURIComponent(urlQ));
        }
      }
    } else {
      setSearchQuery("");
    }
  }, [open, location.search]);

  // Get current company (fallback to cookie)
  const defaultcompany = cookies.get("defaultcompany");
  const selectedCompany = currentCompany || defaultcompany;

  // Handle advanced search dialog open
  const handleAdvancedSearchOpen = () => {
    setAdvancedSearchOpen(true);
  };

  // Handle advanced search dialog close
  const handleAdvancedSearchClose = () => {
    setAdvancedSearchOpen(false);
  };

  // ✅ NEW: Handle closing both modals on successful search
  const handleSearchSuccess = () => {
    setAdvancedSearchOpen(false); // Close advanced search dialog
    onClose(); // Close main search modal
  };

  // Search mutation using quick search API
  const searchMutation = useMutation({
    mutationKey: ["quickSearch"],
    mutationFn: ({ query, categoryID, startDate, endDate }) => {
      let queryParams = `selectedCompany=${selectedCompany}&searchQuery=${query}${tenantString || ""}&pageNumber=1&pageSize=20`;

      if (categoryID) {
        queryParams += `&categoryID=${categoryID}`;
      }

      if (startDate) {
        queryParams += `&startDate=${startDate}`;
      }

      if (endDate) {
        queryParams += `&endDate=${endDate}`;
      }

      return getWithQueryApiServices(apiRoutes.QUICK_SEARCH, queryParams);
    },
    onSuccess: (response, variables) => {
      let queryParams = `selectedCompany=${selectedCompany}&searchQuery=${variables.query}${tenantString || ""}&pageNumber=1&pageSize=20`;
      if (variables.categoryID)
        queryParams += `&categoryID=${variables.categoryID}`;
      if (variables.startDate)
        queryParams += `&startDate=${variables.startDate}`;
      if (variables.endDate) queryParams += `&endDate=${variables.endDate}`;

      navigate(ROUTE_PATHS.SEARCH_RESULT, {
        state: {
          data: response.data?.results || [],
          totalCount: response.data?.totalCount || 0,
          searchValue: searchQuery,
          queryParams,
          searchType: "quick",
        },
      });
      onClose();
    },
    onError: (error) => {
      errorToast(
        error?.response?.data?.message || "Search failed. Please try again.",
      );
    },
  });

  const handleSearch = (
    e,
    categoryID = null,
    startDate = null,
    endDate = null,
  ) => {
    e?.preventDefault();

    if (!selectedCompany) {
      errorToast("Please select a company first");
      return;
    }

    if (!searchQuery.trim() || searchQuery.trim().length < 1) {
      if (!searchQuery.trim()) {
        // Allow clearing search
        searchMutation.mutate("");
      } else {
        errorToast("Search query must be at least 1 character");
        return;
      }
    }

    searchMutation.mutate({
      query: searchQuery,
      categoryID,
      startDate,
      endDate,
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch(e);
    }
  };

  return (
    <>
      <StyledDialog
        open={open}
        onClose={onClose}
        fullScreen
        disablePortal={true}
        hideBackdrop={true}
        disableEnforceFocus={true}
        disableRestoreFocus={true}
        sx={{ pointerEvents: "none" }}
        PaperProps={{ sx: { pointerEvents: "auto" } }}
      >
        <DialogContent sx={{ padding: 0 }}>
          <SearchContainer>
            {/* Search Box */}
            <SearchBox>
              <Box
                component="form"
                onSubmit={handleSearch}
                sx={{ position: "relative" }}
              >
                 <StyledTextField
                  id="tour-search-input"
                  fullWidth
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={searchMutation.isPending}
                  autoFocus
                  error={searchQuery.length > 0 && searchQuery.length < 1}
                  helperText={
                    searchQuery.length > 0 && searchQuery.length < 1
                      ? "Minimum 1 character required"
                      : ""
                  }
                  sx={{
                    "& .MuiInputBase-input": {
                      paddingLeft: 0,
                    },
                    "& .MuiFormHelperText-root": {
                      marginLeft: 0,
                      position: "absolute",
                      top: "100%",
                      marginTop: "10px",
                      whiteSpace: "nowrap",
                    },
                  }}
                />

                {/* Advanced Search Button */}
                <AdvancedSearchButton
                  id="tour-advanced-search-btn"
                  onClick={handleAdvancedSearchOpen}
                  disabled={searchMutation.isPending}
                >
                  <TuneIcon
                    sx={{ fontSize: { xs: 20, sm: 24 }, stroke: "#7285b3", strokeWidth: 0.7 }}
                  />
                </AdvancedSearchButton>

                {/* Submit Search Button */}
                <SubmitSearchButton
                  id="tour-submit-search-btn"
                  onClick={handleSearch}
                  disabled={searchQuery.trim().length < 1 || searchMutation.isPending}
                >
                  <SearchIcon
                    sx={{ fontSize: { xs: 20, sm: 24 } }}
                  />
                </SubmitSearchButton>
              </Box>
            </SearchBox>

            {/* Loading indicator */}
            {searchMutation.isPending && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.75)",
                  zIndex: 9999,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  backdropFilter: "blur(3px)",
                }}
              >
                <CircularProgress size={50} sx={{ mb: 2, color: "#7286b1" }} />
                <Typography
                  variant="h6"
                  color="#7286b1"
                  sx={{ fontWeight: "bold" }}
                >
                  Searching documents...
                </Typography>
              </Box>
            )}

            {/* Instructions */}
            <Typography
              sx={{
                textAlign: "center",
                mt: { xs: 2, sm: 4 },
                color: "#4a4a4a",
                maxWidth: "500px",
                fontWeight: 600,
                letterSpacing: "0.2px",
                fontSize: { xs: "12px", sm: "14px" },
              }}
            >
              Click{" "}
              <TuneIcon
                sx={{
                  fontSize: { xs: 16, sm: 20 },
                  verticalAlign: "middle",
                  mx: 0.5,
                  stroke: "#7285b3",
                  strokeWidth: 0.8,
                }}
              />{" "}
              for advanced search options.
              {!selectedCompany && (
                <Box
                  component="span"
                  sx={{ display: "block", color: "#d32f2f", mt: 1 }}
                >
                  Please select a company to search.
                </Box>
              )}
            </Typography>
          </SearchContainer>
        </DialogContent>
      </StyledDialog>

      <AdvancedSearchDialog
        open={advancedSearchOpen}
        onClose={handleAdvancedSearchClose}
        maxWidth={false}
        disableEnforceFocus={true}
        scroll="body"
      >
        <DateFilter
          searchValue={searchQuery}
          setSearchValue={setSearchQuery}
          setOpen={setAdvancedSearchOpen}
          currentCompany={currentCompany}
          tenantString={tenantString}
          onSearchSuccess={handleSearchSuccess} // ✅ NEW PROP
        />
      </AdvancedSearchDialog>
    </>
  );
};
